import { temporal } from 'zundo';
import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';
import { mutative } from 'zustand-mutative';
import { createAnimationSlice } from '~/features/animation/slice';
import { createAtlasSlice } from '~/features/atlas/slice';
import { createBackgroundSlice } from '~/features/background/slice';
import { createClipsSlice } from '~/features/clips/slice';
import { createEditorSlice } from '~/features/editor/slice';
import { createReferencePointSlice } from '~/features/frames/referencePoint/slice';
import { createFramesSlice } from '~/features/frames/slice';
import { createGridSlice } from '~/features/grid/slice';
import { createPinSlice } from '~/features/pin/slice';
import { createSourceSlice } from '~/features/source/slice';
import { createWatermarkSlice } from '~/features/watermark/slice';
import { createWorkflowSlice } from '~/features/workflow/slice';
import { clampClipFps } from '~/lib/utils';
import { historyEqual, pickHistory, pickSettings, type Settings } from './partialize';
import type { RootState } from './slices';

export const HISTORY_LIMIT = 100;

export function resetSession(): void {
  const s = useStore.getState();
  s.resetSource();
  s.resetBackground();
  s.resetGrid();
  s.resetWatermark();
  s.resetClips();
  s.resetFrames();
  s.resetAnimation();
  s.resetAtlas();
  s.resetEditor();
  s.resetWorkflow();
  s.resetPin();
  s.resetReferencePoint();
}

export const useStore = create<RootState>()(
  persist(
    temporal(
      mutative((...a) => ({
        ...createSourceSlice(...a),
        ...createBackgroundSlice(...a),
        ...createGridSlice(...a),
        ...createWatermarkSlice(...a),
        ...createClipsSlice(...a),
        ...createFramesSlice(...a),
        ...createAnimationSlice(...a),
        ...createAtlasSlice(...a),
        ...createEditorSlice(...a),
        ...createWorkflowSlice(...a),
        ...createPinSlice(...a),
        ...createReferencePointSlice(...a),
      })),
      {
        partialize: pickHistory,
        limit: HISTORY_LIMIT,
        equality: historyEqual,
      },
    ),
    {
      name: 'spriteer-settings',
      version: 5,
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => pickSettings(state) as unknown as RootState,
      migrate: (persisted, version) => {
        type MigratingClip = Settings['clips']['list'][number] & {
          fps?: number;
          rowIndices?: number[];
        };
        type MigratingSettings = {
          pin?: { framingMode?: unknown };
          frames?: { alignmentMode?: unknown };
          referencePoint?: { patch?: unknown };
          clips?: { list?: MigratingClip[]; activeId?: string | null };
          atlas?: { layoutMode?: Settings['atlas']['layoutMode'] };
          grid?: { colDividers?: number[] | null };
        };
        const p = (persisted ?? {}) as MigratingSettings;
        if (version < 2 && p.pin && p.pin.framingMode === 'grid') {
          // 'grid' literal renamed to 'uniform' to disambiguate from LayoutMode 'grid'.
          p.pin.framingMode = 'uniform';
        }
        if (version < 3) {
          if (p.frames) delete p.frames.alignmentMode;
          if (p.referencePoint) delete p.referencePoint.patch;
          p.clips = { list: [], activeId: null };
        }
        if (version < 4) {
          if (p.atlas?.layoutMode?.kind === 'grid') {
            p.atlas = { ...(p.atlas ?? {}), layoutMode: { kind: 'sequence' } };
          }
          const list = p.clips?.list;
          if (Array.isArray(list)) {
            p.clips = {
              list: list.map((clip) => ({
                ...clip,
                fps: clampClipFps(clip.fps),
              })),
              activeId: p.clips?.activeId ?? null,
            };
          }
        }
        if (version < 5) {
          // rowIndices: number[] → source: { kind: 'cells', cells: BoxRef[] }.
          // Expand each row into all (row, col) cells using persisted colDividers
          // (the only reliable colCount we have at migrate time). Drop legacy clips
          // that have neither rowIndices nor source — nothing meaningful to recover.
          const list = p.clips?.list;
          if (Array.isArray(list)) {
            const cols = p.grid?.colDividers;
            const colCount = Array.isArray(cols) && cols.length >= 2 ? cols.length - 1 : 1;
            const migrated: MigratingClip[] = [];
            for (const clip of list) {
              if ('source' in clip && clip.source) {
                migrated.push(clip);
                continue;
              }
              const rows = clip.rowIndices;
              if (!Array.isArray(rows) || rows.length === 0) continue;
              const cells: { row: number; col: number }[] = [];
              for (const row of rows) {
                if (!Number.isInteger(row) || row < 0) continue;
                for (let c = 0; c < colCount; c++) cells.push({ row, col: c });
              }
              if (cells.length === 0) continue;
              const { rowIndices: _rowIndices, ...rest } = clip;
              migrated.push({
                ...rest,
                source: { kind: 'cells', cells },
              } as MigratingClip);
            }
            const prevActive = p.clips?.activeId ?? null;
            const survives = prevActive && migrated.some((c) => c.id === prevActive);
            p.clips = {
              list: migrated,
              activeId: survives ? prevActive : (migrated[0]?.id ?? null),
            };
          }
        }
        return p as MigratingSettings as RootState;
      },
      // Persisted state covers a Settings subset — overlay it onto the live
      // slice defaults so runtime-only fields (currentFrame, derived buffers,
      // …) are never replaced with `undefined` on rehydrate.
      merge: (persisted, current) => {
        const p = (persisted ?? {}) as Partial<Settings>;
        return {
          ...current,
          source: { ...current.source, hash: p.source?.hash ?? null },
          background: { ...current.background, ...(p.background ?? {}) },
          grid: { ...current.grid, ...(p.grid ?? {}) },
          editor: { ...current.editor, ...(p.editor ?? {}) },
          watermark: { ...current.watermark, ...(p.watermark ?? {}) },
          clips: { ...current.clips, ...(p.clips ?? {}) },
          frames: { ...current.frames, ...(p.frames ?? {}) },
          animation: { ...current.animation, ...(p.animation ?? {}) },
          atlas: { ...current.atlas, ...(p.atlas ?? {}) },
          workflow: { ...current.workflow, ...(p.workflow ?? {}) },
          pin: {
            ...current.pin,
            ...(p.pin ?? {}),
            detecting: false,
            blobs: null,
          },
        };
      },
    },
  ),
);
